/**
 * PLP product-card image carousel.
 *
 * Native horizontal scroll-snap track ([data-card-gallery-track]) inside each
 * [data-card-gallery]. Mobile = swipe; desktop = hover chevrons that scroll the
 * track. A scroll progress bar ([data-card-gallery-bar-thumb]) tracks position
 * continuously. Each slide is a link, so tap → PDP and swipe → scroll are handled
 * natively by the browser.
 */
(function () {
  function updateGallery(track) {
    const gallery = track.closest('[data-card-gallery]');
    if (!gallery) return;

    const scrollable = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= scrollable - 2;

    // Progress bar: thumb width = visible fraction, position = scroll fraction.
    const thumb = gallery.querySelector('[data-card-gallery-bar-thumb]');
    if (thumb && track.scrollWidth > 0) {
      const widthPct = (track.clientWidth / track.scrollWidth) * 100;
      const leftPct = (track.scrollLeft / track.scrollWidth) * 100;
      thumb.style.width = widthPct + '%';
      thumb.style.left = leftPct + '%';
    }

    // No wrap-around: hide prev at the start, next at the end.
    const prev = gallery.querySelector('[data-card-gallery-prev]');
    const next = gallery.querySelector('[data-card-gallery-next]');
    if (prev) prev.classList.toggle('is-hidden', atStart);
    if (next) next.classList.toggle('is-hidden', atEnd);
  }

  // Warm only the NEXT slide of a card once it's near the viewport, so the first
  // swipe is instant. The remaining slides stay loading=lazy (browser-managed) so
  // large PLPs don't load dozens of full-res images at once and exhaust memory on
  // mobile. We flip the real <img> to eager (no duplicate Image() objects).
  const preloadObserver =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          function (entries, obs) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              obs.unobserve(entry.target);
              const next = entry.target.querySelectorAll('.card-gallery__img')[1];
              if (next) next.setAttribute('loading', 'eager');
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  function initTrack(track) {
    if (track.dataset.galleryBound === 'true') return;
    track.dataset.galleryBound = 'true';

    if (preloadObserver) preloadObserver.observe(track);

    let ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          updateGallery(track);
          ticking = false;
        });
      },
      { passive: true }
    );

    // Initial paint (and again after layout settles / images size the track).
    updateGallery(track);
    requestAnimationFrame(function () {
      updateGallery(track);
    });
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-card-gallery-track]').forEach(initTrack);
  }

  // Chevrons scroll the track by one slide (delegated, so no per-card binding).
  document.addEventListener('click', function (event) {
    const prev = event.target.closest('[data-card-gallery-prev]');
    const next = event.target.closest('[data-card-gallery-next]');
    if (!prev && !next) return;

    const gallery = (prev || next).closest('[data-card-gallery]');
    const track = gallery && gallery.querySelector('[data-card-gallery-track]');
    if (!track) return;

    event.preventDefault();
    event.stopPropagation();
    track.scrollBy({ left: (next ? 1 : -1) * track.clientWidth, behavior: 'smooth' });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Bind galleries added later (AJAX filtering, infinite scroll, etc.).
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-card-gallery-track]')) initTrack(node);
          if (node.querySelectorAll) initAll(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
